import type { ExternalUserFormData } from "@/schemas/users/external-user";
import { cn } from "@/utils/theme";
import {
  Avatar,
  AvatarFallback,
  Heading,
  Icon,
} from "@elaniintc/goes-design-system";
import type { ReactNode } from "react";
import { useFormContext } from "react-hook-form";

const IconWrapper = ({ children }: { children: ReactNode }) => {
  return (
    <div className="size-7 rounded-lg bg-neutral-background border border-neutral-quiet flex items-center justify-center text-body">
      {children}
    </div>
  );
};

interface Props {
  selectedUserType: {
    label: string;
    value: string;
  } | null;
}

const ExternalUserDetails = ({ selectedUserType }: Props) => {
  const { watch } = useFormContext<ExternalUserFormData>();
  const data = watch();

  const showLastName = data.document_type !== "NIT";
  const isLegalPerson = data.document_type === "NIT";

  const fullName = isLegalPerson
    ? data.commercial_name
      ? `${data.first_name || ""} (${data.commercial_name})`.trim()
      : data.first_name || ""
    : showLastName
      ? `${data.first_name || ""} ${data.last_name || ""}`.trim()
      : data.first_name || "";

  const getDocumentLabel = () => {
    switch (data.document_type) {
      case "DUI":
        return "Número de DUI";
      case "NIT":
        return "Número de NIT";
      case "PASSPORT":
        return "Número de pasaporte";
      case "CARNE_INSTITUCIONAL":
        return "Número de carné";
      default:
        return "Número de documento";
    }
  };

  return (
    <div className="p-4">
      <div className="flex flex-col gap-4 pb-4">
        <Avatar size="xl">
          <AvatarFallback color="action">
            {data.first_name?.charAt(0) || data.last_name?.charAt(0) || ""}
          </AvatarFallback>
        </Avatar>
        <div>
          <Heading
            variant="h5"
            className={cn("text-on-disabled", fullName && "text-heading")}
          >
            {fullName ||
              (isLegalPerson
                ? "Razón social (Nombre comercial)"
                : "Nombre y apellido")}
          </Heading>
          <p
            className={cn(
              "text-base leading-3xl text-body-quiet",
              selectedUserType && "text-on-disabled",
            )}
          >
            {selectedUserType?.label || "Tipo de usuario"}
          </p>
        </div>
      </div>
      <div className="w-full h-px border-t border-neutral-quiet" />
      <div className="flex flex-col gap-2 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <IconWrapper>
              <Icon type="far" name="id-card" />
            </IconWrapper>
            <span
              className={cn(
                "text-body font-normal",
                !data.document_number && "text-on-disabled",
              )}
              data-testid="profile-document"
            >
              {data.document_number || getDocumentLabel()}
            </span>
          </div>
        </div>
        {data.document_type !== "NIT" && (
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <IconWrapper>
                <Icon type="far" name="birthday-cake" />
              </IconWrapper>
              <span
                className={cn(
                  "text-body font-normal",
                  !data.birthdate && "text-on-disabled",
                )}
                data-testid="profile-birthdate"
              >
                {data.birthdate
                  ? (() => {
                      try {
                        const date = new Date(data.birthdate);
                        if (!Number.isNaN(date.getTime())) {
                          return `${String(date.getDate()).padStart(2, "0")}/${String(date.getMonth() + 1).padStart(2, "0")}/${date.getFullYear()}`;
                        }
                      } catch {
                        return data.birthdate;
                      }
                      return data.birthdate;
                    })()
                  : "Fecha de nacimiento"}
              </span>
            </div>
          </div>
        )}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <IconWrapper>
              <Icon type="far" name="envelope" />
            </IconWrapper>
            <span
              className={cn(
                "text-body font-normal",
                !data.email && "text-on-disabled",
              )}
              data-testid="profile-email"
            >
              {data.email || "Correo electrónico"}
            </span>
          </div>
        </div>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <IconWrapper>
              <Icon type="far" name="phone" />
            </IconWrapper>
            <span
              className={cn(
                "text-body font-normal",
                !data.phone && "text-on-disabled",
              )}
              data-testid="profile-phone"
            >
              {data.phone?.number
                ? `+(${data.phone.calling_code}) ${data.phone.number}`
                : "Número de teléfono"}
            </span>
          </div>
        </div>
      </div>
      <div className="w-full h-px border-t border-neutral-quiet" />
    </div>
  );
};

export default ExternalUserDetails;
